
import { defineComponent } from 'vue';
import Dialog from 'primevue/dialog';
import Galleria from 'primevue/galleria';
import Rating from 'primevue/rating';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import { mapActions, mapGetters } from 'vuex';
import Image from 'primevue/image';
import ProgressSpinner from 'primevue/progressspinner';
import PartsService from '@/services/PartsService';
import PartRelationService from '@/services/PartRelationService';
import PartOptionGroupService from '@/services/PartOptionGroupService';
import POSPartsTable from './POSPartsTable.vue';
import RelatedPartsTableSkeleton from './RelatedPartsTableSkeleton.vue';
import CollapsibleSection from '@/components/UI/CollapsibleSection.vue';
import Utils from '@/utility/utils';
import { PartRelationItem } from '@/types/partRelation';
import { PartOptionGroupItem } from '@/types/partOptionGroup';

export default defineComponent({
  name: 'ProductDialog',
  components: {
    Dialog,
    Galleria,
    Rating,
    InputNumber,
    Button,
    Divider,
    Image,
    ProgressSpinner,
    POSPartsTable,
    RelatedPartsTableSkeleton,
    CollapsibleSection,
  },
  props: {
    open: {
      type: Boolean,
      required: true
    },
    selectedProduct: {
      type: Object,
      required: true
    },
    customerId: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      getClient: "session/getClient",
      getCustomer: "customer/getCust", 
      getSelectedCoCode: "control/getSelectedCoCode",
      getMRK: "control/getMRK"
    }),
    totalAmount(): string {
      return (this.productInfo.product_price * this.productQty).toFixed(2);
    },
    numImagesVisible(): number {
      if(this.productInfo.images.length > 3){
        return 3;
      }
      if(this.productInfo.images.length === 1){
        return 1;
      }
      return 2;
    },
    showRelatedParts(): boolean {
      return (this.relatedParts.relations && this.relatedParts.relations.length > 0) ||
             (this.relatedParts.option_groups && this.relatedParts.option_groups.length > 0);
    },
    groupedRelatedParts() {
      // Explicitly reference trigger to ensure reactivity
      void this.relatedPartsDataUpdateTrigger;
      
      const grouped: Record<string, { group_info: PartOptionGroupItem; relations: any[]; expanded: boolean }> = {};
      
      // Group relations by option_group_id
      this.relatedParts.relations.forEach((relation: PartRelationItem) => {
        const groupId = relation.option_group_id || 'ungrouped';
        
        if (!grouped[groupId]) {
          const groupInfo = this.relatedParts.option_groups.find((g: PartOptionGroupItem) => g.optiongroup_id === groupId) || {
            optiongroup_id: groupId,
            opt_grp_desc: `Group ${groupId}`,
            req_selection: 'N'
          };
          
          // Initialize expanded state if not already set
          if (!(groupId in this.groupExpandedStates)) {
            this.groupExpandedStates[groupId] = this.getMRK?.pos_collapse_part_option_groups === 'Y' ? false : true; 
          }
          
          grouped[groupId] = {
            group_info: groupInfo,
            relations: [],
            expanded: this.groupExpandedStates[groupId]
          };
        }
        
        const partKey = relation.related_part_no;
        
        // Use stored part data if available, otherwise create new
        let posTableItem = this.relatedPartsData[partKey as string];
        if (!posTableItem) {
          posTableItem = {
            part_number: relation.related_part_no,
            part_no: relation.related_part_no,
            altcode1: relation.altcode1,
            desc: relation.related_desc1,
            wrap_desc: relation.related_desc1,
            quantity: parseInt(relation.default_qty || '1'),
            price: '0.00',
            required: relation.required,
            image: '', // Will be populated by fetchPrices call
            avail_qty: null, // Use null or undefined to indicate stock data not yet loaded; will be updated by API
            std_price_items: [], // Price data from fetchPrices
            code_items: [], // Code price data from fetchPrices
            // Include relation data for display
            option_group_id: relation.option_group_id,
            default_qty: relation.default_qty
          };
          // Store the new item
          this.relatedPartsData[partKey as string] = posTableItem;
        }
        
        // Push the actual relatedPartsData object reference so updates are reactive
        // This ensures when avail_qty is updated by API calls, the table sees the change
        grouped[groupId].relations.push(this.relatedPartsData[partKey as string]);
      });
      
      return grouped;
    }
  },
  data() {
    return {
      loading: false,
      showProductDetails: false,
      productQty: 1,
      productInfo: {
        part_no: '',
        altcode1: '',
        manufactured_part: '',
        rating: 0,
        total_ratings: '0',
        answered_questions: 0,
        product_price: 0,
        resources: [],
        product_characteristics: [] as any,
        images: [] as any,
        wrap_desc: '',
        invloc: [],
      },
      // Related parts functionality
      loadingRelatedParts: false,
      relatedParts: {
        relations: [] as PartRelationItem[],
        option_groups: [] as PartOptionGroupItem[]
      },
      partOptionGroupHeader: '',
      selectedRelatedParts: [] as string[],
      selectedOptions: {} as Record<string, any>,
      groupExpandedStates: {} as Record<string, boolean>,
      // Store part quantities and full part data to preserve user changes
      relatedPartsData: {} as Record<string, any>,
      // Add a reactive trigger to force computed property updates
      relatedPartsDataUpdateTrigger: 0,
      responsiveOptions: [
        {
            breakpoint: '991px',
            numVisible: 4
        },
        {
            breakpoint: '767px',
            numVisible: 3
        },
      ],
      // Services
      partsService: null as any,
      partRelationService: null as any,
      partOptionGroupService: null as any
    };
  },
  created() {
    // Initialize services
    this.partsService = new PartsService();
    this.partRelationService = new PartRelationService();
    this.partOptionGroupService = new PartOptionGroupService();
    
    // Watch for dialog open/close
    this.$watch('open', (newVal: boolean) => {
      this.showProductDetails = newVal;
      if (newVal) {
        this.loadProductData();
      }
    }, { immediate: true });
    
    // Watch for product changes
    this.$watch('selectedProduct', (newProduct: any) => {
      if (newProduct && this.open) {
        this.loadProductData();
      }
    }, { deep: true });
  },
  emits: ['closeProductDialog', 'addProductToCart'],
  methods: {
    ...mapActions({
      addNotification: "notification/add",
      fetchPrices: "pos/fetchPrices"
    }),
    closeDialog() {
      this.showProductDetails = false;
      this.cleanupDialogData();
      this.$emit('closeProductDialog');
    },
    
    cleanupDialogData() {
      // Stop any ongoing API calls by resetting loading states
      this.loading = false;
      this.loadingRelatedParts = false;
      
      // Clear related parts data to prevent stale data
      this.relatedParts = {
        relations: [] as PartRelationItem[],
        option_groups: [] as PartOptionGroupItem[]
      };
      this.relatedPartsData = {};
      this.selectedRelatedParts = [];
      this.selectedOptions = {};
      this.groupExpandedStates = {};
      
      // Reset reactive trigger
      this.relatedPartsDataUpdateTrigger = 0;
    },
    addProductToCart() {
      // Validate required option groups before adding to cart
      const validationResult = this.validateRequiredSelections();
      if (!validationResult.isValid) {
        this.addNotification({
          title: 'Required Parts Missing',
          message: validationResult.message,
          type: 'error'
        });
        return;
      }
      
      const cartItem = {
        ...this.selectedProduct, 
        quantity: this.productQty,
        relatedParts: this.selectedRelatedParts.map(partNo => ({
          part_no: partNo,
          quantity: this.getSelectedPartQuantity(partNo)
        })),
        selectedOptions: this.selectedOptions
      };
      this.$emit('addProductToCart', cartItem);
      
      // Auto-add required parts to cart
      this.addRequiredPartsToCart();
      
      this.closeDialog();
    },
    calculateStep(part: any) {
      return Utils.calculateQtyStep(part);
    },
    
    // Extract the product data loading logic from updated() hook
    loadProductData() {
      if (!this.selectedProduct?.part_number || !this.open) {
        return;
      }
      
      // Update product info from props
      this.productInfo.part_no = this.selectedProduct.altcode1 || this.selectedProduct.part_number;
      this.productInfo.wrap_desc = this.selectedProduct.desc;
      this.productInfo.product_price = parseFloat(this.selectedProduct.price) || 0;
      this.productQty =
        this.selectedProduct.quantity ||
        parseFloat(this.selectedProduct.sell_qty) ||
        1;
      
      // Reset loading state
      this.productInfo.manufactured_part = '';
      this.productInfo.product_characteristics = [];
      this.loading = true;

      this.partsService.getPart(this.selectedProduct.part_number, this.customerId, "spec_desc").then((resp: any) => {
        // Don't process response if dialog was closed during API call
        if (!this.open) {
          return;
        }
        
        this.productInfo.manufactured_part = resp.miva_product_id ? resp.miva_product_id : '';
        this.productInfo.product_characteristics = resp.specs_items; 
        if(resp.spec_desc && resp.spec_desc.length > 0) {
          for(let spec = 0; spec < resp.specs_items?.length; spec++) {
            if(resp.spec_desc[spec] != "") {
              this.productInfo.product_characteristics[spec].specs_desc = resp.spec_desc[spec] 
            }
          }
        }
        this.productInfo.resources = resp.external_links
        this.productInfo.images = resp.image_desc_items === undefined ? [] : resp.image_desc_items;
        this.productInfo.images.forEach((image: any) => {
          if(!image.image_path.includes("http")) {
            image.image_path = require("@/assets/images/productDefaultImg.svg")
            image.image_desc = 'Default image'
          }
        });
        
        // Load related parts and option groups
        this.loadRelatedParts();
        
      }).catch(() => {
        this.showProductDetails = false;
        this.addNotification({
          title: 'Error',
          message: 'Part does not exist.',
          type: 'error'
        })
      })
      .finally(() => {
        this.loading = false;
      });
    },
    
    // Related parts methods
    async loadRelatedParts() {
      if (!this.selectedProduct?.part_number || !this.open) {
        return; // Don't load if dialog is closed or no part number
      }
      
      this.loadingRelatedParts = true;
      this.relatedParts = {
        relations: [] as PartRelationItem[],
        option_groups: []
      };
      // Clear stored data when loading new parts
      this.relatedPartsData = {};
      
      try {
        // First get option groups for the part
        const optionsResponse = await this.partOptionGroupService.fetchPartOptionGroups({
          id: this.selectedProduct.part_number,
          customerId: this.getCustomer?.code || this.getCustomer?.cust_id
        });
        this.relatedParts.option_groups = optionsResponse.partoptiongroup_items || [];
        this.partOptionGroupHeader = optionsResponse.header || "";
        
        // Then get relations for each option group
        if (this.relatedParts.option_groups.length > 0) {
          const relationPromises = this.relatedParts.option_groups.map((group : any) => 
            this.partRelationService.fetchPartRelation({ 
              partId: this.selectedProduct.part_number,
              optionGroupId: group.optiongroup_id , 
              customerId: this.getCustomer?.code || this.getCustomer?.cust_id
            })
          );
          
          const relationResponses = await Promise.all(relationPromises);
          this.relatedParts.relations = relationResponses.flatMap((response: any) => 
            response.partrelation_items || []
          );
        }
        
        // Trigger computed property to create initial objects
        this.relatedPartsDataUpdateTrigger++;
        
        // Force a nextTick to ensure computed property has evaluated
        await this.$nextTick();
        
        // Force the computed property to evaluate by accessing it
        // This creates the initial objects in relatedPartsData before API calls
        void this.groupedRelatedParts;
        
        // After getting relations, fetch details for each related part including images
        await this.fetchRelatedPartDetails();
        
      } catch (error) {
        console.log('Error loading related parts:', error);
      } finally {
        // Always set loading to false after everything is complete
        this.loadingRelatedParts = false;
      }
    },
    
    fetchRelatedPartDetails(): Promise<void> {
      return new Promise((resolve) => {
        // Don't fetch if dialog is closed
        if (!this.open) {
          resolve();
          return;
        }
        
        // Get unique part numbers from relations
        const partNumbers = [...new Set(this.relatedParts.relations.map(r => r.related_part_no))];
        
        // Use fetchPrices call like POS.vue to get all part details including images
        let completedPromises = 0;
        const totalPromises = partNumbers.length;
        
        // If no parts to fetch, resolve immediately
        if (totalPromises === 0) {
          resolve();
          return;
        }
        
        // For each part, call fetchPrices to get complete data including images
        partNumbers.forEach((partNo) => {
          // Find the relation for this part to get altcode1 if available
          const relation = this.relatedParts.relations.find(r => r.related_part_no === partNo);
          const searchPartNo = relation?.altcode1 || partNo;
          
          // Use same fetchPrices pattern as POS.vue
          this.fetchPrices({
            client: this.getClient,
            code: this.getCustomer?.code || this.getCustomer?.cust_id,
            cat: '', // No category filter for specific part lookup
            part_no: searchPartNo, // Use altcode1 if available, otherwise partNo
            web_cats: '',
            customerId: this.customerId,
            rangeStart: 1,
            rangeEnd: 1, // Only get this specific part
            coCode: this.getSelectedCoCode
          }).then((pricesResponse: any) => {
            // Don't process response if dialog was closed during API call
            if (!this.open) {
              return;
            }
            
            // If we get price data for this part, update the stored part data
            if (pricesResponse.parts && pricesResponse.parts.length > 0) {
              const partData = pricesResponse.parts[0]; // Should be our specific part
              
              if (this.relatedPartsData[partNo as string]) {
                // Update with price data including images and availability
                this.relatedPartsData[partNo as string].image = partData.image || '';
                
                // Map availability data and ensure it's a number
                this.relatedPartsData[partNo as string].avail_qty = 
                  Number(partData.avail_qty) || 0;
                
                // Trigger immediate reactive update for this part's stock data
                this.relatedPartsDataUpdateTrigger++;
                
                this.relatedPartsData[partNo as string].std_price_items = partData.std_price_items || [];
                this.relatedPartsData[partNo as string].code_items = partData.code_items || [];
                
                // Update description if available
                if (partData.desc || partData.wrap_desc) {
                  this.relatedPartsData[partNo as string].desc = partData.desc || partData.wrap_desc;
                  this.relatedPartsData[partNo as string].wrap_desc = partData.wrap_desc || partData.desc;
                }
                
                // Calculate initial price using same logic as POS.vue
                const std_price = this.getStdPrice(this.relatedPartsData[partNo as string]);
                const code_price = this.getCodePrice(this.relatedPartsData[partNo as string]);
                if (code_price) {
                  this.relatedPartsData[partNo as string].price = code_price;
                } else {
                  this.relatedPartsData[partNo as string].price = std_price;
                }
                
                // Fallback to default image if no valid image
                if (!this.relatedPartsData[partNo as string].image || !this.relatedPartsData[partNo as string].image.includes("http")) {
                  this.relatedPartsData[partNo as string].image = require("@/assets/images/productDefaultImg.svg");
                }
              }
            }
          }).catch((error) => {
            // Set default value for failed API call
            if (this.relatedPartsData[partNo as string]) {
              this.relatedPartsData[partNo as string].avail_qty = 0;
              this.relatedPartsDataUpdateTrigger++;
            }
            // Fallback: try to get basic part details if prices call fails
            this.partsService.getPart(partNo, this.customerId, "spec_desc").then((partDetails: any) => {
              // Don't process response if dialog was closed during API call
              if (!this.open) {
                return;
              }
              
              if (this.relatedPartsData[partNo as string]) {
                this.relatedPartsData[partNo as string].image = partDetails.image_desc_items && partDetails.image_desc_items.length > 0 
                  ? partDetails.image_desc_items[0].image_path 
                  : require("@/assets/images/productDefaultImg.svg");
                this.relatedPartsData[partNo as string].avail_qty = partDetails.invloc && partDetails.invloc.length > 0 
                  ? Number(partDetails.invloc[0].avail_qty) || 0 
                  : 0;
              }
            }).catch((fallbackError: any) => {
              // Even if fallback fails, set avail_qty to 0 rather than undefined
              if (this.relatedPartsData[partNo as string]) {
                this.relatedPartsData[partNo as string].avail_qty = 0;
                // Trigger reactive update
                this.relatedPartsDataUpdateTrigger++;
              }
              // No completion logic here; handled in outer finally
            });
          }).finally(() => {
            completedPromises++;
            if (completedPromises === totalPromises) {
              // Trigger reactive update after all data is fetched
              this.relatedPartsDataUpdateTrigger++;
              // Resolve the Promise when all parts are processed
              resolve();
            }
          });
        });
      });
    },
    
    toggleOptionGroup(groupId: string, value: boolean) {
      this.groupExpandedStates[groupId] = value;
    },
    
    getGroupHeader(groupInfo: PartOptionGroupItem, groupId: string): string {
      return groupInfo.opt_grp_desc || `Group ${groupId}`;
    },
    
    onRelatedPartAddToCart(part: any) {
      // Create a cart item for the related part similar to the main product
      const relatedCartItem = {
        part_number: part.part_number || part.part_no,
        desc: part.desc || part.wrap_desc || part.part_number || part.part_no,
        wrap_desc: part.wrap_desc || part.desc || part.part_number || part.part_no,
        quantity: part.quantity || 1,
        price: part.price || '0.00',
        sell_qty: '1',
        altcode1: part.altcode1 || part.part_number || part.part_no
      };
      
      // Emit to parent to add to cart (same as main Add to Cart)
      this.$emit('addProductToCart', relatedCartItem);
      
      // Also track in selected related parts for the main product
      if (!this.selectedRelatedParts.includes(part.part_number || part.part_no)) {
        this.selectedRelatedParts.push(part.part_number || part.part_no);
      }
      
      // Store the selected quantity
      this.selectedOptions[`qty_${part.part_number || part.part_no}`] = part.quantity || 1;
      
      // Show success notification
      this.addNotification({
        title: 'Success',
        message: `${part.part_number || part.part_no} added to cart`,
        type: 'success'
      });
    },
    
    getSelectedPartQuantity(partNo: string): number {
      return this.selectedOptions[`qty_${partNo}`] || 1;
    },
    
    validateRequiredSelections(): { isValid: boolean; message: string } {
      const requiredGroups = Object.values(this.groupedRelatedParts).filter(
        group => group.group_info.req_selection === 'Y'
      );
      
      for (const group of requiredGroups) {
        const hasSelection = group.relations.some(part => 
          this.selectedRelatedParts.includes(part.part_number || part.part_no)
        );
        
        if (!hasSelection) {
          return {
            isValid: false,
            message: `Please select at least one part from required group: ${group.group_info.opt_grp_desc}`
          };
        }
      }
      
      return { isValid: true, message: '' };
    },
    
    addRequiredPartsToCart() {
      // Auto-add required parts that haven't been manually added
      Object.values(this.groupedRelatedParts).forEach(group => {
        if (group.group_info.req_selection === 'Y') {
          group.relations.forEach(part => {
            if (part.required === 'Y' && !this.selectedRelatedParts.includes(part.part_number || part.part_no)) {
              // Auto-add required part with default quantity
              const requiredCartItem = {
                part_number: part.part_number || part.part_no,
                desc: part.desc || part.wrap_desc || part.part_number || part.part_no,
                wrap_desc: part.wrap_desc || part.desc || part.part_number || part.part_no,
                quantity: part.quantity || 1,
                price: part.price || '0.00',
                sell_qty: '1',
                altcode1: part.altcode1 || part.part_number || part.part_no
              };
              
              this.$emit('addProductToCart', requiredCartItem);
              
              // Add to selected parts tracking
              this.selectedRelatedParts.push(part.part_number || part.part_no);
              this.selectedOptions[`qty_${part.part_number || part.part_no}`] = part.quantity || 1;
              
              // Show notification for auto-added required parts
              this.addNotification({
                title: 'Required Part Added',
                message: `${part.part_number || part.part_no} (required) added to cart`,
                type: 'info'
              });
            }
          });
        }
      });
    },
    
    isGroupRequired(groupInfo: PartOptionGroupItem): boolean {
      return groupInfo.req_selection === 'Y';
    },
    
    isPartRequired(part: any): boolean {
      return part.required === 'Y';
    },
    
    isSelectionRequiredAndMissing(groupInfo: PartOptionGroupItem, groupId: string): boolean {
      // Check if the group requires selection
      if (groupInfo.req_selection !== 'Y') {
        return false;
      }
      
      // Check if any parts from this group have been selected/added to cart
      const groupData = this.groupedRelatedParts[groupId];
      if (!groupData || !groupData.relations) {
        return true; // Required but no parts available
      }
      
      // Check if any part from this group has been selected
      const hasSelection = groupData.relations.some(part => 
        this.selectedRelatedParts.includes(part.part_number || part.part_no)
      );
      
      // If required but no selection made, show warning
      return !hasSelection;
    },
    
    // Price calculation methods (same as POSPartsTable and POS.vue)
    getStdPrice(price: any) {
      const quantity = price.quantity || 1;
      let price_value = "";
      if (price.std_price_items) {
        if (price.std_price_items.length === 1) {
          price_value = price.std_price_items[0].std_price;
        } else {
          price.std_price_items.forEach((std_item: any) => {
            const std_quantity = parseFloat(std_item.std_qty);
            if (quantity >= std_quantity) {
              price_value = std_item.std_price;
            }
          });
        }

        // If there is no valid price with quantitys default to first option.
        if (price_value === "") {
          price_value = price.std_price_items[0].std_price;
        }
      }
      if (price_value) {
        return price_value;
      }
      return "0";
    },
    
    getCodePrice(price: any) {
      const code = this.getCustomer?.code || this.getCustomer?.cust_id
      const quantity = price.quantity || 1;
      const code_price = price.code_items?.find(
        (item: any) => item.code === code
      );
      let price_value = "";
      if (code_price && code_price.code_price_items) {
        if (code_price.code_price_items.length === 1) {
          price_value = code_price.code_price_items[0].code_price;
        } else {
          code_price.code_price_items.forEach((code_item: any) => {
            const code_quantity = parseFloat(code_item.code_qty);
            if (quantity >= code_quantity) {
              price_value = code_item.code_price;
            }
          });
        }
      }

      if (price_value) {
        return price_value;
      }
      return "";
    }
  }
})
